import React, { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { importGuestsFromCSV } from "../utils/helperFunctions";
import { toast } from "react-toastify";

const UploadCsv = () => {
  const { hotelId } = useParams();
  const location = useLocation();
  const hotelName = location.state?.hotelName;
  const navigate = useNavigate();
  const [uploading, setupLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);


  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    validateFile(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const selectedFile = e.dataTransfer.files[0];
    validateFile(selectedFile);
  };

  const validateFile = (selectedFile) => {
    setError("");
    if (!selectedFile) return;
    if (
      !["text/csv", "application/vnd.ms-excel"].includes(selectedFile.type) &&
      !selectedFile.name.endsWith(".csv")
    ) {
      setError("Please upload a valid CSV file");
      return;
    }
    // if (selectedFile.size > 1024 * 1024) {
    //   setError("File size exceeds 1MB limit");
    //   return;
    // }
    setFile(selectedFile);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a file first");
      return;
    }

    setupLoading(true);

    try {
      const formData = new FormData();
      formData.append("csvFile", file);
      formData.append("hotelId", hotelId);
      const response = await importGuestsFromCSV(formData);
      if(response.data.status){
      toast.success(`Successfully imported ${response.data.count} guests and skiped ${response.data.skipped}`);
      setFile(null);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.error || 'Failed to import guests');
    } finally {
      setupLoading(false);
    }
  };


  const downloadSampleCSV = () => {
    const csvContent = [
      "First Name,Middle Name,Last Name,Company Name,Date of Birth,Mobile No.,Phone No.,Document Type Name,Document No.,Email,House Flat No.,Address,City,District,State Name,Zip Code,Country Name,Locality,Coming From,Going To,Checkin Date",
      'jayesh,,raval,,2024-01-04,8238701774,8238701774,Aadhar Card,906875305367,JAYESH@GMAIL.COM,,"SAHKTI VIJAY SOC. NANA VARCHHA , SURAT, ",SURAT,SURAT,Gujarat,395006,India,NANA VARCHHA,SURAT,SURAT,2025-05-25'
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "guest_data_sample.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50">
      <div className="container mx-auto bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Guest Data Upload</h1>
              <p className="text-sm text-gray-600 mt-1">
                Upload CSV file to add multiple guests to hotel {hotelName}
              </p>
            </div>
            
          </div>
        </div>
        <div className="">
          <div className="bg-white p-6 shadow-sm ">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-8 bg-blue-50 border border-blue-100 rounded-lg p-4"
            >
              <h2 className="text-lg font-semibold text-blue-800 mb-3">Instructions</h2>
              <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
                <li>File must be in CSV format</li>
                <li>Ensure the CSV follows the required format (download sample below)</li>
                <li>Required columns: First Name, Middle Name, Last Name, Company Name, Date of Birth,
                  mobile No., Phone No., Document Type Name, Document No., Email,
                  House Flat No., Address, City, District, State Name, Zip Code,
                  Country Name, Locality, Coming From, Going To, Checkin Date</li>
                <li>Date format should be YYYY-MM-DD</li>

              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="mb-8"
            >
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-all 
              ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-indigo-300'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="flex flex-col items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 text-indigo-500 mb-3"
                    fill="none"
                    viewBox=" 0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-lg font-medium text-gray-700 mb-1">
                    {file ? file.name : "Drag & drop your CSV file here"}
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    or click to browse files
                  </p>
                  <label className="cursor-pointer bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors">
                    Select File
                    <input
                      type="file"
                      className="hidden"
                      accept=".csv, text/csv, application/vnd.ms-excel"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>

              {error && (
                <p
                  className="mt-4 p-3 text-red-700 rounded-md text-sm"
                >
                  {error}
                </p>
              )}
            </motion.div>


            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="flex flex-col sm:flex-row justify-between items-center gap-4"
            >
              <button
                onClick={downloadSampleCSV}
                className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Download Sample CSV
              </button>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/hotel')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!file || uploading}
                  className={`px-6 py-2 rounded-md text-white font-medium transition-colors 
                ${!file || uploading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                >
                  {uploading ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Uploading...
                    </span>
                  ) : (
                    "Upload File"
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadCsv;